import { useState, useEffect } from "react";
import {
    Percent,
    CheckCircle,
    RotateCcw,
    Plus,
    Minus
} from "lucide-react";
import { useRegion } from "../context/RegionContext";

export default function Calculators() {
    const { region, setRegion, allRegions } = useRegion();
    const [activeTab, setActiveTab] = useState("sip");

    const isINR = region.currency === "INR";

    // 1. SIP Calculator States
    const [sipAmount, setSipAmount] = useState(() => (isINR ? 5000 : 500));
    const [sipRate, setSipRate] = useState(12);
    const [sipYears, setSipYears] = useState(10);

    // 2. Loan EMI Calculator States
    const [loanAmount, setLoanAmount] = useState(() => (isINR ? 100000 : 50000));
    const [loanRate, setLoanRate] = useState(9.5);
    const [loanYears, setLoanYears] = useState(5);

    // 3. Tax Calculator States
    const [gstAmount, setGstAmount] = useState(() => (isINR ? 10000 : 1000));
    const [gstRate, setGstRate] = useState(() => region.defaultTaxRate || 18);
    const [gstType, setGstType] = useState("exclusive"); // exclusive, inclusive

    // Automatically synchronize calculator when region changes (e.g. Canada CAD, UK GBP, US USD)
    useEffect(() => {
        setGstRate(region.defaultTaxRate || 18);
        if (region.currency === "INR") {
            setSipAmount(5000);
            setLoanAmount(100000);
            setGstAmount(10000);
        } else {
            setSipAmount(500);
            setLoanAmount(50000);
            setGstAmount(1000);
        }
    }, [region.code, region.defaultTaxRate, region.currency]);

    // --- MATH CALCULATIONS ---

    // SIP Formula: M = P * [((1 + i)^n - 1) / i] * (1 + i)
    const calculateSIP = () => {
        const P = parseFloat(sipAmount);
        const R = parseFloat(sipRate);
        const Y = parseFloat(sipYears);

        if (isNaN(P) || isNaN(R) || isNaN(Y) || P <= 0 || R <= 0 || Y <= 0) {
            return { totalInvested: 0, totalValue: 0, estReturns: 0 };
        }

        const monthlyRate = R / 12 / 100;
        const totalMonths = Y * 12;

        const totalValue = P * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) * (1 + monthlyRate);
        const totalInvested = P * totalMonths;
        const estReturns = totalValue - totalInvested;

        return {
            totalInvested: Math.round(totalInvested),
            totalValue: Math.round(totalValue),
            estReturns: Math.round(estReturns)
        };
    };

    // EMI Formula: E = P * r * (1 + r)^n / ((1 + r)^n - 1)
    const calculateEMI = () => {
        const P = parseFloat(loanAmount);
        const R = parseFloat(loanRate);
        const Y = parseFloat(loanYears);

        if (isNaN(P) || isNaN(R) || isNaN(Y) || P <= 0 || R <= 0 || Y <= 0) {
            return { monthlyEMI: 0, totalPayable: 0, totalInterest: 0 };
        }

        const monthlyRate = R / 12 / 100;
        const totalMonths = Y * 12;

        const emi = P * monthlyRate * (Math.pow(1 + monthlyRate, totalMonths) / (Math.pow(1 + monthlyRate, totalMonths) - 1));
        const totalPayable = emi * totalMonths;
        const totalInterest = totalPayable - P;

        return {
            monthlyEMI: Math.round(emi),
            totalPayable: Math.round(totalPayable),
            totalInterest: Math.round(totalInterest)
        };
    };

    // GST / Tax Formulas
    const calculateGST = () => {
        const A = parseFloat(gstAmount);
        const R = parseFloat(gstRate);

        if (isNaN(A) || isNaN(R) || A <= 0 || R < 0) {
            return { gstAmountVal: 0, netVal: 0, totalVal: 0 };
        }

        let gstAmountVal = 0;
        let netVal = 0;
        let totalVal = 0;

        if (gstType === "exclusive") {
            gstAmountVal = A * (R / 100);
            netVal = A;
            totalVal = A + gstAmountVal;
        } else {
            gstAmountVal = A - (A / (1 + (R / 100)));
            netVal = A - gstAmountVal;
            totalVal = A;
        }

        return {
            gstAmountVal: Math.round(gstAmountVal * 100) / 100,
            netVal: Math.round(netVal * 100) / 100,
            totalVal: Math.round(totalVal * 100) / 100
        };
    };

    const sipResults = calculateSIP();
    const emiResults = calculateEMI();
    const gstResults = calculateGST();

    const formatCurrency = (val) => {
        return new Intl.NumberFormat(region.locale, {
            style: "currency",
            currency: region.currency,
            maximumFractionDigits: 0
        }).format(val);
    };

    return (
        <div className="space-y-8">
            {/* Header Layout: Tab Controls and Currency Selector */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-200/20 dark:border-slate-800/50 pb-6">
                {/* Tab Controls */}
                <div className="flex flex-wrap sm:flex-nowrap justify-center p-1.5 rounded-2xl bg-slate-500/10 dark:bg-white/5 border border-slate-200/50 dark:border-slate-800/50 backdrop-blur-xl gap-1">
                    {[
                        { id: "sip", label: "Mutual Fund / Investment" },
                        { id: "emi", label: "Loan Repayment (EMI)" },
                        { id: "gst", label: `${region.taxName} Calculator` }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-3.5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                                activeTab === tab.id
                                    ? "bg-white dark:bg-slate-900 text-accent shadow-md"
                                    : "text-secondary hover:text-primary dark:text-gray-400 dark:hover:text-white"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Country & Currency Switcher Toolbar */}
                <div className="flex flex-wrap p-1 rounded-2xl bg-slate-500/10 dark:bg-white/5 border border-slate-200/50 dark:border-slate-800/50 backdrop-blur-xl self-center lg:self-auto gap-1">
                    {Object.values(allRegions).map((r) => (
                        <button
                            key={r.code}
                            onClick={() => setRegion(r.code)}
                            className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                region.code === r.code
                                    ? "bg-white dark:bg-slate-900 text-accent shadow-sm"
                                    : "text-secondary hover:text-primary dark:text-gray-400 dark:hover:text-white"
                            }`}
                            title={`${r.name} (${r.currency})`}
                        >
                            <span>{r.flag}</span>
                            <span>{r.currency}</span>
                            <span className="text-[10px] opacity-70">({r.symbol.trim()})</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Calculator Workspace Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* SIP CALCULATOR INPUT */}
                {activeTab === "sip" && (
                    <>
                        <div className="lg:col-span-7 bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/50 dark:border-slate-800/50 shadow-lg space-y-6">
                            <h3 className="text-xl font-bold text-primary dark:text-white flex items-center gap-2 mb-4">
                                Investment & SIP Parameters ({region.currency})
                            </h3>

                            {/* Slider 1: Monthly Investment */}
                            <div className="space-y-3">
                                <div className="flex justify-between items-center text-sm font-bold">
                                    <span className="text-secondary dark:text-gray-400">Monthly Investment</span>
                                    {/* Sleek Custom Stepper */}
                                    <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                        <button
                                            type="button"
                                            onClick={() => setSipAmount((prev) => Math.max(isINR ? 500 : 50, prev - (isINR ? 500 : 50)))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Decrease"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <div className="flex items-center px-2">
                                            <span className="text-xs font-extrabold text-slate-400 select-none mr-1">{region.symbol}</span>
                                            <input
                                                type="number"
                                                value={sipAmount}
                                                onChange={(e) => setSipAmount(Math.max(0, parseInt(e.target.value) || 0))}
                                                className="w-20 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSipAmount((prev) => Math.min(isINR ? 100000 : 10000, prev + (isINR ? 500 : 50)))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Increase"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min={isINR ? 500 : 50}
                                    max={isINR ? 100000 : 10000}
                                    step={isINR ? 500 : 50}
                                    value={sipAmount}
                                    onChange={(e) => setSipAmount(parseInt(e.target.value))}
                                    className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-tertiary">
                                    <span>{formatCurrency(isINR ? 500 : 50)}</span>
                                    <span>{formatCurrency(isINR ? 100000 : 10000)}</span>
                                </div>
                            </div>

                            {/* Slider 2: Rate of Return */}
                            <div className="space-y-3">
                                <div className="flex justify-between items-center text-sm font-bold">
                                    <span className="text-secondary dark:text-gray-400">Expected Return Rate (p.a)</span>
                                    <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                        <button
                                            type="button"
                                            onClick={() => setSipRate((prev) => Math.max(1, Math.round((prev - 0.5) * 10) / 10))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Decrease"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <div className="flex items-center px-2">
                                            <input
                                                type="number"
                                                step="0.5"
                                                value={sipRate}
                                                onChange={(e) => setSipRate(Math.max(0.1, parseFloat(e.target.value) || 0))}
                                                className="w-12 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                            />
                                            <span className="text-xs font-bold text-slate-400 select-none">%</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSipRate((prev) => Math.min(30, Math.round((prev + 0.5) * 10) / 10))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Increase"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min="1"
                                    max="30"
                                    step="0.5"
                                    value={sipRate}
                                    onChange={(e) => setSipRate(parseFloat(e.target.value))}
                                    className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-tertiary">
                                    <span>1%</span>
                                    <span>30%</span>
                                </div>
                            </div>

                            {/* Slider 3: Years */}
                            <div className="space-y-3">
                                <div className="flex justify-between items-center text-sm font-bold">
                                    <span className="text-secondary dark:text-gray-400">Time Period (Years)</span>
                                    <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                        <button
                                            type="button"
                                            onClick={() => setSipYears((prev) => Math.max(1, prev - 1))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Decrease"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <div className="flex items-center px-2">
                                            <input
                                                type="number"
                                                value={sipYears}
                                                onChange={(e) => setSipYears(Math.max(1, parseInt(e.target.value) || 0))}
                                                className="w-12 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                            />
                                            <span className="text-[11px] font-bold text-slate-400 select-none">Yr</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSipYears((prev) => Math.min(40, prev + 1))}
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                            title="Increase"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="40"
                                        step="1"
                                        value={sipYears}
                                        onChange={(e) => setSipYears(parseInt(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] text-tertiary">
                                        <span>1 Year</span>
                                        <span>40 Years</span>
                                    </div>
                                </div>
                            </div>

                            {/* SIP RESULTS PANEL */}
                            <div className="lg:col-span-5 bg-gradient-to-br from-slate-50 to-indigo-50/60 dark:from-indigo-950 dark:to-slate-900 text-slate-900 dark:text-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-indigo-950/50 shadow-lg space-y-8 h-full">
                                <h3 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                                    <RotateCcw className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Projected Maturity Summary
                                </h3>

                                <div className="space-y-6">
                                    <div>
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">Total Invested Amount</span>
                                        <h4 className="text-2xl font-black text-slate-900 dark:text-white">{formatCurrency(sipResults.totalInvested)}</h4>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">Est. Wealth Gain</span>
                                        <h4 className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{formatCurrency(sipResults.estReturns)}</h4>
                                    </div>
                                    <div className="pt-6 border-t border-slate-200 dark:border-white/10">
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">Total Maturity Value</span>
                                        <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 dark:from-cyan-400 dark:to-indigo-300">
                                            {formatCurrency(sipResults.totalValue)}
                                        </h2>
                                    </div>
                                </div>

                                {/* Ratio progress bar */}
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs text-slate-600 dark:text-indigo-300 font-bold">
                                        <span>Invested ({Math.round((sipResults.totalInvested / (sipResults.totalValue || 1)) * 100)}%)</span>
                                        <span>Est. Returns ({Math.round((sipResults.estReturns / (sipResults.totalValue || 1)) * 100)}%)</span>
                                    </div>
                                    <div className="w-full h-3 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden flex">
                                        <div
                                            className="bg-indigo-500 h-full transition-all duration-300"
                                            style={{ width: `${(sipResults.totalInvested / (sipResults.totalValue || 1)) * 100}%` }}
                                        ></div>
                                        <div
                                            className="bg-emerald-500 dark:bg-emerald-400 h-full transition-all duration-300"
                                            style={{ width: `${(sipResults.estReturns / (sipResults.totalValue || 1)) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* EMI CALCULATOR INPUT */}
                    {activeTab === "emi" && (
                        <>
                            <div className="lg:col-span-7 bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/50 dark:border-slate-800/50 shadow-lg space-y-6">
                                <h3 className="text-xl font-bold text-primary dark:text-white flex items-center gap-2 mb-4">
                                    Loan Repayment Parameters ({region.currency})
                                </h3>

                                {/* Slider 1: Loan Amount */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm font-bold">
                                        <span className="text-secondary dark:text-gray-400">Loan Principal</span>
                                        <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setLoanAmount((prev) => Math.max(isINR ? 10000 : 1000, prev - (isINR ? 10000 : 1000)))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Decrease"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <div className="flex items-center px-2">
                                                <span className="text-xs font-extrabold text-slate-400 select-none mr-1">{region.symbol}</span>
                                                <input
                                                    type="number"
                                                    value={loanAmount}
                                                    onChange={(e) => setLoanAmount(Math.max(0, parseInt(e.target.value) || 0))}
                                                    className="w-24 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setLoanAmount((prev) => Math.min(isINR ? 5000000 : 1000000, prev + (isINR ? 10000 : 1000)))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Increase"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min={isINR ? 10000 : 1000}
                                        max={isINR ? 5000000 : 1000000}
                                        step={isINR ? 10000 : 1000}
                                        value={loanAmount}
                                        onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] text-tertiary">
                                        <span>{formatCurrency(isINR ? 10000 : 1000)}</span>
                                        <span>{formatCurrency(isINR ? 5000000 : 1000000)}</span>
                                    </div>
                                </div>

                                {/* Slider 2: Interest Rate */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm font-bold">
                                        <span className="text-secondary dark:text-gray-400">Annual Interest Rate</span>
                                        <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setLoanRate((prev) => Math.max(1, Math.round((prev - 0.25) * 100) / 100))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Decrease"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <div className="flex items-center px-2">
                                                <input
                                                    type="number"
                                                    step="0.25"
                                                    value={loanRate}
                                                    onChange={(e) => setLoanRate(Math.max(0.1, parseFloat(e.target.value) || 0))}
                                                    className="w-12 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                                />
                                                <span className="text-xs font-bold text-slate-400 select-none">%</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setLoanRate((prev) => Math.min(30, Math.round((prev + 0.25) * 100) / 100))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Increase"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="30"
                                        step="0.1"
                                        value={loanRate}
                                        onChange={(e) => setLoanRate(parseFloat(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] text-tertiary">
                                        <span>1%</span>
                                        <span>30%</span>
                                    </div>
                                </div>

                                {/* Slider 3: Loan Tenure */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm font-bold">
                                        <span className="text-secondary dark:text-gray-400">Tenure (Years)</span>
                                        <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setLoanYears((prev) => Math.max(1, prev - 1))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Decrease"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <div className="flex items-center px-2">
                                                <input
                                                    type="number"
                                                    value={loanYears}
                                                    onChange={(e) => setLoanYears(Math.max(1, parseInt(e.target.value) || 0))}
                                                    className="w-12 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                                />
                                                <span className="text-[11px] font-bold text-slate-400 select-none">Yr</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setLoanYears((prev) => Math.min(30, prev + 1))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Increase"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="30"
                                        step="1"
                                        value={loanYears}
                                        onChange={(e) => setLoanYears(parseInt(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] text-tertiary">
                                        <span>1 Year</span>
                                        <span>30 Years</span>
                                    </div>
                                </div>
                            </div>

                            {/* EMI Results Panel */}
                            <div className="lg:col-span-5 bg-gradient-to-br from-slate-50 to-indigo-50/60 dark:from-indigo-950 dark:to-slate-900 text-slate-900 dark:text-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-indigo-950/50 shadow-lg space-y-8">
                                <h3 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                                    <CheckCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Loan Breakdown
                                </h3>

                                <div className="space-y-6">
                                    <div>
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">Monthly Payment (EMI)</span>
                                        <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 dark:from-indigo-200 dark:to-cyan-300">
                                            {formatCurrency(emiResults.monthlyEMI)}
                                        </h2>
                                    </div>

                                    <div className="pt-6 border-t border-slate-200 dark:border-indigo-800/60 grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="text-xs text-slate-500 dark:text-indigo-300 block mb-1 font-semibold">Principal Amount</span>
                                            <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(loanAmount)}</p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-500 dark:text-indigo-300 block mb-1 font-semibold">Total Interest</span>
                                            <p className="text-xl font-bold text-indigo-600 dark:text-indigo-300">{formatCurrency(emiResults.totalInterest)}</p>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-slate-200 dark:border-indigo-800/60 flex justify-between items-center">
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-wider font-semibold">Total Amount Payable</span>
                                        <span className="text-xl font-black text-emerald-600 dark:text-emerald-400">{formatCurrency(emiResults.totalPayable)}</span>
                                    </div>

                                    {/* Visual distribution bar */}
                                    <div className="w-full bg-slate-200 dark:bg-indigo-950/80 rounded-full h-3 flex overflow-hidden">
                                        <div
                                            className="bg-indigo-500 h-full transition-all duration-300"
                                            style={{ width: `${(loanAmount / (emiResults.totalPayable || 1)) * 100}%` }}
                                        ></div>
                                        <div
                                            className="bg-indigo-300 h-full transition-all duration-300"
                                            style={{ width: `${(emiResults.totalInterest / (emiResults.totalPayable || 1)) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* TAX CALCULATOR INPUT */}
                    {activeTab === "gst" && (
                        <>
                            <div className="lg:col-span-7 bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/50 dark:border-slate-800/50 shadow-lg space-y-6">
                                <h3 className="text-xl font-bold text-primary dark:text-white flex items-center gap-2 mb-4">
                                    {region.taxName} Calculator ({region.currency})
                                </h3>

                                {/* net cost amount */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm font-bold">
                                        <span className="text-secondary dark:text-gray-400">Cost / Base Amount</span>
                                        <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setGstAmount((prev) => Math.max(isINR ? 100 : 10, prev - (isINR ? 500 : 50)))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Decrease"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <div className="flex items-center px-2">
                                                <span className="text-xs font-extrabold text-slate-400 select-none mr-1">{region.symbol}</span>
                                                <input
                                                    type="number"
                                                    value={gstAmount}
                                                    onChange={(e) => setGstAmount(Math.max(0, parseInt(e.target.value) || 0))}
                                                    className="w-24 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-sm py-1"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setGstAmount((prev) => Math.min(isINR ? 500000 : 50000, prev + (isINR ? 500 : 50)))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Increase"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min={isINR ? 100 : 10}
                                        max={isINR ? 500000 : 50000}
                                        step={isINR ? 100 : 10}
                                        value={gstAmount}
                                        onChange={(e) => setGstAmount(parseInt(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] text-tertiary">
                                        <span>{formatCurrency(isINR ? 100 : 10)}</span>
                                        <span>{formatCurrency(isINR ? 500000 : 50000)}</span>
                                    </div>
                                </div>

                                {/* Calculation Type Buttons */}
                                <div className="space-y-2">
                                    <span className="text-sm font-bold text-secondary dark:text-gray-400 block">Calculation Method</span>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => setGstType("exclusive")}
                                            className={`p-4 rounded-xl border text-center font-bold text-sm transition-all cursor-pointer ${
                                                gstType === "exclusive"
                                                    ? "bg-accent/10 border-accent text-accent"
                                                    : "border-slate-200 dark:border-slate-800 text-secondary"
                                            }`}
                                        >
                                            Add {region.taxName} (+ Exclusive)
                                        </button>
                                        <button
                                            onClick={() => setGstType("inclusive")}
                                            className={`p-4 rounded-xl border text-center font-bold text-sm transition-all cursor-pointer ${
                                                gstType === "inclusive"
                                                    ? "bg-accent/10 border-accent text-accent"
                                                    : "border-slate-200 dark:border-slate-800 text-secondary"
                                            }`}
                                        >
                                            Remove {region.taxName} (- Inclusive)
                                        </button>
                                    </div>
                                </div>

                                {/* Tax Rate selection */}
                                <div className="space-y-2">
                                    <span className="text-sm font-bold text-secondary dark:text-gray-400 block">
                                        {region.taxName} Rate ({region.name})
                                    </span>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {(region.taxOptions || []).map((opt, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setGstRate(opt.rate)}
                                                className={`px-3 sm:px-4 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all cursor-pointer ${
                                                    gstRate === opt.rate
                                                        ? "bg-accent text-white shadow-sm"
                                                        : "bg-slate-500/5 dark:bg-white/5 text-secondary hover:bg-slate-500/10 dark:hover:bg-white/10"
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        step="0.25"
                                        value={gstRate}
                                        onChange={(e) => setGstRate(parseFloat(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-slate-200 dark:bg-slate-800 accent-accent cursor-pointer"
                                    />
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-secondary dark:text-gray-400 font-semibold">Custom {region.taxName} Rate</span>
                                        <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shadow-sm p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setGstRate((prev) => Math.max(0, Math.round((prev - 0.25) * 100) / 100))}
                                                className="w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Decrease"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <div className="flex items-center px-1.5">
                                                <input
                                                    type="number"
                                                    step="0.25"
                                                    value={gstRate}
                                                    onChange={(e) => setGstRate(Math.max(0, parseFloat(e.target.value) || 0))}
                                                    className="w-10 bg-transparent text-center font-bold text-accent dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-xs py-0.5"
                                                />
                                                <span className="text-[10px] font-bold text-slate-400 select-none">%</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setGstRate((prev) => Math.min(50, Math.round((prev + 0.25) * 100) / 100))}
                                                className="w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                                title="Increase"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* GST / Tax Results Panel */}
                            <div className="lg:col-span-5 bg-gradient-to-br from-slate-50 to-indigo-50/60 dark:from-indigo-950 dark:to-slate-900 text-slate-900 dark:text-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-indigo-950/50 shadow-lg space-y-8 h-full">
                                <h3 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                                    <Percent className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> {region.taxName} Breakdown ({region.currency})
                                </h3>

                                <div className="space-y-6">
                                    <div>
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">
                                            {gstType === "exclusive" ? "Net Price (Base)" : "Net Price (Pre-tax)"}
                                        </span>
                                        <h4 className="text-2xl font-black text-slate-900 dark:text-white">{formatCurrency(gstResults.netVal)}</h4>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">
                                            Tax Amount ({region.taxName})
                                        </span>
                                        <h4 className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{formatCurrency(gstResults.gstAmountVal)}</h4>
                                    </div>
                                    <div className="pt-6 border-t border-slate-200 dark:border-white/10">
                                        <span className="text-xs text-slate-500 dark:text-indigo-300 uppercase tracking-widest font-semibold block mb-1">
                                            {gstType === "exclusive" ? "Total Price (Inclusive)" : "Total Price (Sales Amount)"}
                                        </span>
                                        <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 dark:from-cyan-400 dark:to-indigo-300">
                                            {formatCurrency(gstResults.totalVal)}
                                        </h2>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
        </div>
    );
}
